import {
  Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ToastService } from 'src/app/services/toast.service';
import { ValidationService } from 'src/app/services/validation.service';
import {
  buildValidationSheetCsv,
  validationSheetFilename,
  parseAttendanceSheet,
  AttendanceSheetEntry,
} from 'src/app/utils/validation-sheet.utils';

const MAX_SHEET_BYTES = 5 * 1024 * 1024;

/**
 * A sign-in sheet attendee who was never registered for this course, matched to
 * an account and staged to be added when the admin presses Save.
 */
export interface StagedWalkIn {
  email: string;
  userId: number;
  firstName: string;
  lastName: string;
  /** True when the account already had a registration we will credit instead. */
  alreadyRegistered: boolean;
  adjustedValue: number;
  /** Sign-in times from the sheet, carried through untouched. */
  timeIn: string;
  timeOut: string;
}

/** A sheet entry that cannot be added — an unknown or inactive email. */
export interface RejectedWalkIn {
  email: string;
  reason: string;
  message: string;
}

@Component({
  selector: 'app-course-validation-details',
  templateUrl: './course-validation-details.component.html',
  styleUrls: [ './course-validation-details.component.scss' ],
})
export class CourseValidationDetailsComponent implements OnInit, OnDestroy {
  tableTitles = [
    { name: 'Full Name', sortBy: 'firstName' },
    {
      name: 'Email', sortBy: 'loginName', class: 'email',
    },
    { name: 'Hours', sortBy: 'adjustedValue' },
    {
      name: 'Status', sortBy: 'currentStatusText', class: 'status',
    },
    // Sign-in times are recorded, not judged — nothing sorts or checks them.
    { name: 'Time In', class: 'time' },
    { name: 'Time Out', class: 'time' },
  ]
  sortOrder = { sortBy: 'currentStatusText', direction: 'ASC' };
  statusPriority: Record<string, number> = {
    registered: 1,
    pending: 2,
    'take survey': 3,
    completed: 4,
    missed: 5,
    cancelled: 6,
  };
  isLoading = true;
  sessionValidationDetails: any;
  selectedSession: any
  updatedValidations: {
    user: any;
    statusKid: string;
    adjustedValue: number;
    timeIn: string;
    timeOut: string;
  }[] = [];
  originalValidations: {
    user: any;
    originalStatusKid: string;
    originalAdjustedValue: number;
    originalTimeIn: string;
    originalTimeOut: string;
  }[] = [];
  bulkSubscription: any;
  walkInSubscription: Subscription;
  validationSubscription: Subscription;
  /** Sign-in sheet attendees to register on Save. */
  walkIns: StagedWalkIn[] = [];
  /** Sheet entries we could not match to an account, shown until dismissed. */
  rejectedWalkIns: RejectedWalkIn[] = [];
  isResolvingWalkIns = false;
  @ViewChild('validationSheetInput') sheetInput?: ElementRef<HTMLInputElement>;

  constructor(
    private validationService: ValidationService,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CourseValidationDetailsComponent>,
  ) { }



  ngOnInit(): void {
    this.selectedSession = this.data
    this.validationService.clearSessionValidationDetails();
    this.validationService.getSessionValidationDetails(this.data);

    this.validationSubscription = this.validationService.validationSessionData$
      .subscribe((response: any) => {
        if (!response) return;

        this.sessionValidationDetails = response;
        this?.sessionValidationDetails?.rows.forEach((user: any) => {
          const cloneUser = JSON.parse(JSON.stringify(user));

          this.originalValidations.push({
            user: cloneUser,
            originalStatusKid: cloneUser.currentStatusKid,
            originalAdjustedValue: cloneUser.adjustedValue,
            originalTimeIn: this.timeValue(cloneUser.timeIn),
            originalTimeOut: this.timeValue(cloneUser.timeOut),
          })

          this?.updateIndividualValidation(user, user.currentStatusKid);
        })
        this.sortColumn('currentStatusText');
      })

  }

  closeDialog = () => {
    this.dialogRef.close();
  }

  sortColumn = (sortBy: string) => {
    if (!this.sessionValidationDetails?.rows) return;

    const newDirection = this.sortOrder.sortBy === sortBy
      ? (this.sortOrder.direction === 'ASC' ? 'DESC' : 'ASC')
      : 'ASC';

    this.sortOrder = { sortBy, direction: newDirection };

    this.sessionValidationDetails.rows.sort((a: any, b: any) => {
      let comparison: number;

      if (sortBy === 'currentStatusText') {
        const priorityA = this.statusPriority[String(a[sortBy] ?? '').toLowerCase()] ?? 99;
        const priorityB = this.statusPriority[String(b[sortBy] ?? '').toLowerCase()] ?? 99;
        comparison = priorityA - priorityB;
      } else if (sortBy === 'firstName') {
        comparison = String(a.firstName ?? '').localeCompare(String(b.firstName ?? ''))
          || String(a.lastName ?? '').localeCompare(String(b.lastName ?? ''));
      } else {
        const valA = a[sortBy];
        const valB = b[sortBy];
        const numA = Number(valA);
        const numB = Number(valB);
        comparison = !isNaN(numA) && !isNaN(numB)
          ? numA - numB
          : String(valA ?? '').localeCompare(String(valB ?? ''));
      }

      return this.sortOrder.direction === 'ASC' ? -comparison : comparison;
    });
  }

  updateIndividualValidation = (user: any, validation: any) => {
    // if the user is already in the updatedValidations array, remove it
    const userIndex = this.updatedValidations.findIndex((updatedValidation: any) => updatedValidation.user.loginName === user.loginName);
    if (userIndex !== -1) {
      this.updatedValidations.splice(userIndex, 1);
    }
    // if the user is unmarked, don't add them to the updatedValidations array
    if (validation === null) {
      return;
    }
    // add the updated user to the updatedValidations array. Times are read off
    // the row itself, so re-marking someone never drops what was typed.
    const updatedValidation = {
      user: user,
      statusKid: validation,
      adjustedValue: Number(user.adjustedValue),
      timeIn: this.timeValue(user.timeIn),
      timeOut: this.timeValue(user.timeOut),
    }
    // add the updated user to the updatedValidations array
    this.updatedValidations.push(updatedValidation);
  }

  approveAll = () => {
    this.sessionValidationDetails.rows.forEach((user: any) => {
      this.updateIndividualValidation(user, 'COMPLETED');
    })
  }

  unmarkAll = () => {
    this.sessionValidationDetails.rows.forEach((user: any) => {
      this.updateIndividualValidation(user, 'REGISTERED');
    })
  }

  denyAll = () => {
    this.sessionValidationDetails.rows.forEach((user: any) => {
      this.updateIndividualValidation(user, 'NOT_TAKEN');
    })
  }


  // these are functions for styling the buttons
  isApproved = (user: any) => {
    const isApprovedArray = this?.updatedValidations?.filter((validatedUser: any) => validatedUser.user.loginName === user.loginName && validatedUser.statusKid === 'COMPLETED');
    return isApprovedArray.length > 0;
  }

  isUnmarked = (user: any) => {
    const isUnmarkedArray = this?.updatedValidations?.filter((validatedUser: any) => validatedUser.user.loginName === user.loginName);
    return !(isUnmarkedArray.length > 0);
  }

  isDenied = (user: any) => {
    const isDeniedArray = this?.updatedValidations?.filter((validatedUser: any) => validatedUser.user.loginName === user.loginName && validatedUser.statusKid === 'NOT_TAKEN');
    return isDeniedArray.length > 0;
  }

  // this styles the select all buttons
  styleButton = (status: any) => {
    if (status === 'COMPLETED') {
      const markedCompleted = this.updatedValidations.filter((validatedUser: any) => validatedUser.validation === 'COMPLETED');
      const isSelected = markedCompleted.length > 0 && markedCompleted.length === this.sessionValidationDetails?.rows?.length;
      return isSelected ? true : false;
    } else if (status === 'NOT_TAKEN') {
      const markedDenied = this.updatedValidations.filter((validatedUser: any) => validatedUser.validation === 'NOT_TAKEN');
      const isSelected = markedDenied.length > 0 && markedDenied.length === this.sessionValidationDetails?.rows?.length;
      return isSelected ? true : false;
    }
    return
  }

  updateAdjustedValue(user: any, event: Event) {
    const inputElement = event.target as HTMLInputElement;
    let eventValue = Number(inputElement.value);
    if (eventValue < 0) {
      eventValue = 0;
      inputElement.value = '0';
    }
    const updatedValidation = this.updatedValidations.find((updatedValidation: any) => {
      return updatedValidation.user.loginName === user.loginName;
    });
    if (updatedValidation) {
      updatedValidation.adjustedValue = eventValue;
    }
    user.adjustedValue = eventValue;
  }

  /** Times are free text: trimmed, never parsed, and empty means "not recorded". */
  timeValue = (value: any) => String(value ?? '').trim();

  updateTime(
    user: any, event: Event, field: 'timeIn' | 'timeOut',
  ) {
    const inputElement = event.target as HTMLInputElement;
    const value = this.timeValue(inputElement.value);

    user[field] = value;

    const updatedValidation = this.updatedValidations.find((updatedValidation: any) => {
      return updatedValidation.user.loginName === user.loginName;
    });
    if (updatedValidation) {
      updatedValidation[field] = value;
    }
  }

  /** Copy the sheet's times onto a roster row, leaving blank cells alone. */
  applySheetTimes = (user: any, entry: AttendanceSheetEntry) => {
    if (entry.timeIn) {
      this.updateTime(
        user, { target: { value: entry.timeIn } } as any, 'timeIn',
      );
    }
    if (entry.timeOut) {
      this.updateTime(
        user, { target: { value: entry.timeOut } } as any, 'timeOut',
      );
    }
  }

  updateWalkInTime = (
    walkIn: StagedWalkIn, event: Event, field: 'timeIn' | 'timeOut',
  ) => {
    const inputElement = event.target as HTMLInputElement;
    walkIn[field] = this.timeValue(inputElement.value);
  }

  getUpdatedUsers() {
    const originalValidations = this.originalValidations;
    const updatedValidations = this.updatedValidations;


    return updatedValidations.filter((updatedValidation) => {
      const originalValidation = originalValidations.find((originalValidation) => originalValidation.user.loginName === updatedValidation.user.loginName);

      if (!originalValidation) {
        throw new Error('Original validation not found');
      }

      const validationSame = updatedValidation.statusKid === originalValidation.originalStatusKid;
      const adjustedValueSame = updatedValidation.adjustedValue == originalValidation.originalAdjustedValue;
      const timesSame = updatedValidation.timeIn === originalValidation.originalTimeIn
        && updatedValidation.timeOut === originalValidation.originalTimeOut;

      return !(validationSame && adjustedValueSame && timesSame);
    });
  }

  // this saves the updated validations
  saveValidationUpdates = () => {
    this.isLoading = true;

    // Walk-ins have to be registered first: the bulk validate endpoint can only
    // update registrations that already exist.
    if (this.walkIns.length === 0) {
      this.submitValidations([]);
      return;
    }

    this.walkInSubscription = this.validationService.registerWalkIns({
      courseId: this.selectedSession.courseId,
      courseItemId: this.selectedSession.courseItemId,
      emails: this.walkIns.map((walkIn) => walkIn.email),
      adjustedValue: this.defaultWalkInHours(),
    }).subscribe({
      next: (response: any) => {
        const added = response?.data?.added ?? [];
        const rejected = response?.data?.rejected ?? [];

        // Build the hour updates before trimming the staged list, which is
        // where the edited hours live.
        const walkInUpdates = this.buildWalkInUpdates(added);

        // Entries rejected at upload are still rejected, so append rather than
        // replace — the summary should account for the whole sheet.
        this.rejectedWalkIns = [ ...this.rejectedWalkIns, ...rejected ];
        this.walkIns = this.walkIns.filter((walkIn) =>
          rejected.some((entry: RejectedWalkIn) => this.isSameEmail(entry.email, walkIn.email)));

        this.submitValidations(walkInUpdates, added.length);
      },
      error: (error: Error) => {
        this.isLoading = false;
        console.log('error', error);
        this.toastService.setToast({
          text: 'Could not add the sign-in sheet attendees. Nothing was saved.', type: 'error', icon: true,
        });
      },
    })
  }

  /**
   * The walk-ins endpoint registers and completes at the session's default
   * hours and records no times, so a walk-in only needs a second pass through
   * the bulk save when the admin edited the hours or the sheet carried times.
   */
  buildWalkInUpdates = (added: any[]) => {
    const defaultHours = this.defaultWalkInHours();

    return (added ?? []).reduce((updates: any[], entry: any) => {
      const walkIn = this.walkIns.find((staged) => this.isSameEmail(staged.email, entry.email));
      const adjustedValue = Number(walkIn?.adjustedValue ?? defaultHours);
      const timeIn = this.timeValue(walkIn?.timeIn);
      const timeOut = this.timeValue(walkIn?.timeOut);

      const nothingToPatch = adjustedValue === defaultHours && !timeIn && !timeOut;
      if (!entry?.userCourseId || nothingToPatch) return updates;

      return [
        ...updates, {
          id: parseFloat(entry.userCourseId),
          currentStatusKid: 'COMPLETED',
          adjustedValue,
          timeIn,
          timeOut,
        },
      ];
    }, []);
  }

  submitValidations = (walkInUpdates: any[], addedWalkInCount = 0) => {
    const updatedUsers = this.getUpdatedUsers();

    const formattedUpdatedValidations = updatedUsers.map((updatedValidation) => {
      return {
        id: parseFloat(updatedValidation.user.userCourseId),
        currentStatusKid: updatedValidation.statusKid,
        adjustedValue: updatedValidation.adjustedValue,
        timeIn: updatedValidation.timeIn,
        timeOut: updatedValidation.timeOut,
      }
    })

    const updateArr = [ ...formattedUpdatedValidations, ...walkInUpdates ];

    // Everything the sheet asked for was rejected — stay open so the admin can
    // see which entries failed rather than closing on an empty save.
    if (updateArr.length === 0 && addedWalkInCount === 0) {
      this.isLoading = false;
      this.toastService.setToast({
        text: 'Nothing was saved. No attendees could be added or updated.', type: 'warn', icon: true,
      });
      return;
    }

    if (updateArr.length === 0) {
      this.isLoading = false;
      this.dialogRef.close('result');
      this.toastService.setToast({
        text: this.saveSummary(addedWalkInCount), type: 'success', icon: true,
      });
      return;
    }

    // here we are packaging the data to send to the backend
    const validationPackage = {
      courseId: this.selectedSession.courseId,
      courseItemId: this.selectedSession.courseItemId,
      updateArr,
    }
    this.bulkSubscription = this.validationService.bulkValidate(validationPackage).subscribe({
      next: (response: any) => {
        this.dialogRef.close('result');
        this.isLoading = false;
        this.toastService.setToast({
          text: this.saveSummary(addedWalkInCount), type: 'success', icon: true,
        })
      },
      error: (error: Error) => {
        this.isLoading = false;
        console.log('error', error);
        throw error;
      },
    })
  }

  saveSummary = (addedWalkInCount: number) => {
    const added = addedWalkInCount > 0
      ? ` ${addedWalkInCount} attendee${addedWalkInCount === 1 ? '' : 's'} added to the course.`
      : '';
    const rejected = this.rejectedWalkIns.length > 0
      ? ` ${this.rejectedWalkIns.length} sheet entr${this.rejectedWalkIns.length === 1 ? 'y was' : 'ies were'} rejected.`
      : '';

    return `Validations updated successfully.${added}${rejected}`;
  }

  // ---- Bulk sign-in sheet (download / upload) ----

  downloadValidationSheet = () => {
    const rows = this.sessionValidationDetails?.rows ?? [];
    if (rows.length === 0) {
      this.toastService.setToast({
        text: 'There are no registered users to download.', type: 'warn', icon: true,
      });
      return;
    }

    const courseName = this.data?.courseName ?? '';
    const courseItemId = this.data?.courseItemId ?? '';

    const csv = buildValidationSheetCsv(
      courseName, courseItemId, rows,
    );
    const filename = validationSheetFilename(courseName, courseItemId);

    const blob = new Blob([ csv ], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  triggerSheetUpload = () => {
    const input = this.sheetInput?.nativeElement;
    if (!input) return;
    // Clear first so re-selecting the same file still fires (change).
    input.value = '';
    input.click();
  }

  onSheetUpload = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (file.size > MAX_SHEET_BYTES) {
      this.toastService.setToast({
        text: 'That file is too large. Please upload a sign-in sheet under 5MB.',
        type: 'error',
        icon: true,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.applyAttendanceSheet(String(reader.result ?? ''));
    };
    reader.onerror = () => {
      this.toastService.setToast({
        text: 'Could not read the uploaded file. Please try again.', type: 'error', icon: true,
      });
    };
    reader.readAsText(file);
  }

  applyAttendanceSheet = (text: string) => {
    const {
      attendedEmails, entries, invalidFormat, sheetCourseId,
    } = parseAttendanceSheet(text);

    if (invalidFormat) {
      this.toastService.setToast({
        text: 'That file doesn\'t look like a sign-in sheet. It needs an Email column and an Attended column.',
        type: 'error',
        icon: true,
      });
      return;
    }

    // Guard against uploading a sheet downloaded for a different course. The
    // Course ID column is optional (hand-made sheets omit it); we only reject
    // when the sheet carries an ID and it disagrees with this course.
    const currentCourseId = String(this.data?.courseItemId ?? '').trim();
    if (sheetCourseId && currentCourseId && sheetCourseId !== currentCourseId) {
      this.toastService.setToast({
        text: `This sign-in sheet is for a different course (Course ID ${sheetCourseId}). Please upload the sheet downloaded for this course.`,
        type: 'error',
        icon: true,
      });
      return;
    }

    const usersByEmail = new Map<string, any>();
    (this.sessionValidationDetails?.rows ?? []).forEach((user: any) => {
      const email = String(user?.loginName ?? '').trim().toLowerCase();
      if (email) usersByEmail.set(email, user);
    });

    let markedCount = 0;
    let alreadyCompletedCount = 0;
    const offRoster: AttendanceSheetEntry[] = [];

    // A fresh upload replaces whatever the previous one staged.
    this.walkIns = [];
    this.rejectedWalkIns = [];

    entries.forEach((entry) => {
      const user = usersByEmail.get(entry.email);
      if (!user) {
        // Someone who attended a course they were never registered for — the
        // server has to match the email to an account before we can add them.
        // Only the ones marked attended are worth adding.
        if (entry.attended) offRoster.push(entry);
        return;
      }

      // Times come across wherever they are filled in, marked attended or not.
      this.applySheetTimes(user, entry);

      if (!entry.attended) return;

      this.updateIndividualValidation(user, 'COMPLETED');
      // Only count users whose status actually changes — this mirrors what
      // Save will persist, so the toast can't overstate the result.
      if (String(user.currentStatusKid) === 'COMPLETED') {
        alreadyCompletedCount += 1;
      } else {
        markedCount += 1;
      }
    });

    if (offRoster.length === 0) {
      this.reportSheetResult(
        markedCount, alreadyCompletedCount, attendedEmails.size,
      );
      return;
    }

    this.resolveWalkIns(
      offRoster, markedCount, alreadyCompletedCount, attendedEmails.size,
    );
  }

  /**
   * Ask the server which off-roster emails belong to a real account. This is a
   * dry run: nothing is registered until Save, but the admin sees straight away
   * which entries will be rejected.
   */
  resolveWalkIns = (
    offRoster: AttendanceSheetEntry[],
    markedCount: number,
    alreadyCompletedCount: number,
    attendedTotal: number,
  ) => {
    this.isResolvingWalkIns = true;

    this.walkInSubscription = this.validationService.registerWalkIns({
      courseId: this.selectedSession.courseId,
      courseItemId: this.selectedSession.courseItemId,
      emails: offRoster.map((entry) => entry.email),
      dryRun: true,
    }).subscribe({
      next: (response: any) => {
        this.isResolvingWalkIns = false;

        const added = response?.data?.added ?? [];
        const defaultHours = this.defaultWalkInHours();

        this.walkIns = added.map((entry: any) => {
          // Fall back to the names on the sheet when the account has none.
          const sheetEntry = offRoster.find((row) => this.isSameEmail(row.email, entry.email));

          return {
            email: entry.email,
            userId: entry.userId,
            firstName: entry.firstName || sheetEntry?.firstName || '',
            lastName: entry.lastName || sheetEntry?.lastName || '',
            alreadyRegistered: !!entry.alreadyRegistered,
            adjustedValue: defaultHours,
            timeIn: sheetEntry?.timeIn ?? '',
            timeOut: sheetEntry?.timeOut ?? '',
          };
        });
        this.rejectedWalkIns = response?.data?.rejected ?? [];

        this.reportSheetResult(
          markedCount, alreadyCompletedCount, attendedTotal,
        );
      },
      error: (error: Error) => {
        this.isResolvingWalkIns = false;
        console.log('error', error);
        this.toastService.setToast({
          text: 'Could not look up the attendees who are not on the roster. The rest of the sheet was applied.',
          type: 'error',
          icon: true,
        });
      },
    })
  }

  reportSheetResult = (
    markedCount: number, alreadyCompletedCount: number, attendedTotal: number,
  ) => {
    const walkInCount = this.walkIns.length;
    const rejectedCount = this.rejectedWalkIns.length;

    const rejected = rejectedCount > 0
      ? ` ${rejectedCount} entr${rejectedCount === 1 ? 'y' : 'ies'} rejected — see the warning above the list.`
      : '';

    if (markedCount === 0 && walkInCount === 0) {
      let reason: string;
      if (attendedTotal === 0) {
        reason = 'No attendees were marked in the sheet.';
      } else if (alreadyCompletedCount > 0) {
        reason = 'Every matched attendee was already completed.';
      } else {
        reason = 'None of the marked attendees could be matched to an account.';
      }
      this.toastService.setToast({
        text: `${reason} Nothing was changed.${rejected}`, type: 'warn', icon: true,
      });
      return;
    }

    const marked = markedCount > 0
      ? `${markedCount} user${markedCount === 1 ? '' : 's'} marked completed.`
      : '';
    const added = walkInCount > 0
      ? ` ${walkInCount} attendee${walkInCount === 1 ? '' : 's'} will be added to the course.`
      : '';

    this.toastService.setToast({
      text: `${marked}${added}${rejected} Review and press Save to apply.`.trim(),
      type: rejectedCount > 0 ? 'warn' : 'success',
      icon: true,
    });
  }

  /** Hours a walk-in is credited with unless the admin edits the row. */
  defaultWalkInHours = () => Number(this.data?.trackingValue ?? 0) || 0;

  isSameEmail = (a: string, b: string) =>
    String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

  removeWalkIn = (walkIn: StagedWalkIn) => {
    this.walkIns = this.walkIns.filter((staged) => staged !== walkIn);
  }

  updateWalkInHours = (walkIn: StagedWalkIn, event: Event) => {
    const inputElement = event.target as HTMLInputElement;
    let eventValue = Number(inputElement.value);
    if (eventValue < 0) {
      eventValue = 0;
      inputElement.value = '0';
    }
    walkIn.adjustedValue = eventValue;
  }

  dismissWalkInWarnings = () => {
    this.rejectedWalkIns = [];
  }

  ngOnDestroy(): void {
    this.validationSubscription.unsubscribe();
    this.bulkSubscription?.unsubscribe();
    this.walkInSubscription?.unsubscribe();
  }
}
